import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Button, CircularProgress, Container, Pagination, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from '@mui/material';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { format } from 'date-fns';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import * as GeometryHelper from '../../api/geometry/geometryHelper';
import { IGetGeometryPreviewResponse } from '../../api/geometry/iApiResponse';
import { IPaginatedResponse } from '../../api/httpClient';
import { EStatus } from '../../shared/enum';
import { IGeometry } from '../../shared/iProject';
import { useNavigate } from 'react-router-dom';
import { route } from '../../route/Index';

interface IGeometryPanelProps {}

const GeometryPanelContainer = styled(Container)`
    padding: 0;
    flex-grow: 1;
`;

const GeometryPanel = (props: IGeometryPanelProps) => {
    let pageSize = 4;

    let timer = React.useRef<NodeJS.Timeout | null>(null);

    const navigate = useNavigate();

    const { t } = useTranslation();

    const [geometryPreview, setGeometryPreview] = React.useState<IPaginatedResponse<IGetGeometryPreviewResponse>>();
    const [isLoading, setIsLoading] = React.useState(false);
    const [pageNumber, setpageNumber] = React.useState(1);
    const [filterName, setFilterName] = React.useState<string | null>(null);

    const fetchGeometryPreview = async (page: number, filter?: string) => {
        setIsLoading(true);
        let res = await GeometryHelper.getAllGeometryPreviewAsync(page, pageSize, filter);
        setGeometryPreview(res.data);
        setIsLoading(false);
    };

    React.useEffect(() => {
        fetchGeometryPreview(pageNumber);
    }, [pageNumber]);

    const handleGoToGeometry = (id: number) => navigate(route.backofficeGeometry.replace(':param', id.toString()));

    const handleChangeStatus = async (toTest: boolean, geometryId: number) => {
        let geometry = await GeometryHelper.getGeometryByIdAsync(geometryId);
        let payload: IGeometry = {
            ...geometry.data,
            status: toTest ? EStatus.Test : EStatus.Deleted,
        };
        let res = await GeometryHelper.updateGeometryAsync(payload);
        if (res.status == 200) {
            fetchGeometryPreview(pageNumber);
            Swal.fire({
                title: t('updatedGeometry'),
                icon: 'success',
                toast: true,
                position: 'top',
                showConfirmButton: false,
                timer: 2000,
            });
        }
    };

    React.useEffect(() => {
        if (filterName !== null) {
            if (timer.current) {
                clearTimeout(timer.current);
            }
            timer.current = setTimeout(() => {
                fetchGeometryPreview(1, filterName);
            }, 1000);
        }
    }, [filterName]);

    return (
        <GeometryPanelContainer maxWidth='md'>
            <Stack height='100%' direction='column' spacing={4}>
                <Stack direction='row' justifyContent='space-between'>
                    <Typography variant='h1' gutterBottom>
                        {t('geometry')}
                    </Typography>
                    <Button variant='orange' endIcon={<AddCircleIcon />} onClick={() => navigate(route.backofficeGeometry.replace(':param', 'newGeometry'))}>
                        {t('addNewGeometry')}
                    </Button>
                </Stack>
                <TextField
                    id='input-search'
                    placeholder={t('searchName')}
                    InputProps={{
                        type: 'search',
                    }}
                    onChange={e => setFilterName(e.target.value)}
                />
                {isLoading && <CircularProgress />}
                {!isLoading && geometryPreview?.value?.length! > 0 && (
                    <>
                        <Stack flexGrow={1}>
                            <TableContainer>
                                <Table sx={{ minWidth: 650 }} size='small' aria-label='simple table'>
                                    <TableHead variant='greenLightWithoutBorder'>
                                        <TableRow>
                                            <TableCell>{t('name')}</TableCell>
                                            <TableCell align='center'>{t('lastUpdate')}</TableCell>
                                            <TableCell align='right'>{t('enabled')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {geometryPreview!.value.map(x => (
                                            <TableRow key={x.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                <TableCell onClick={() => handleGoToGeometry(x.id)} component='th' scope='row'>
                                                    {x.name}
                                                </TableCell>
                                                <TableCell onClick={() => handleGoToGeometry(x.id)} align='center'>
                                                    {format(x.lastUpdateDate, 'dd MMMM yyyy')}
                                                </TableCell>
                                                <TableCell align='right'>
                                                    <Switch
                                                        onChange={e => handleChangeStatus(e.target.checked, x.id)}
                                                        aria-label={`switch ${x.name}`}
                                                        checked={x.status == EStatus.Public || x.status == EStatus.Test}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Stack>
                        {geometryPreview!.pageCount > 1 && (
                            <Stack alignItems='end'>
                                <Pagination
                                    showFirstButton
                                    showLastButton
                                    variant='outlined'
                                    shape='rounded'
                                    page={geometryPreview?.pageNumber}
                                    count={geometryPreview?.pageCount}
                                    onChange={(e, value) => setpageNumber(value)}
                                />
                            </Stack>
                        )}
                    </>
                )}
                {!isLoading && geometryPreview?.value?.length! == 0 && <div>Empty</div>}
            </Stack>
        </GeometryPanelContainer>
    );
};

export default GeometryPanel;
